import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { DaysOfWeek, ReportLayout } from 'app/modules/reports/reports.types';
import { debounceTime } from 'rxjs';

@Component({
  selector: 'app-report-schedule-form',
  standalone: true,
  imports: [
    MatLegacyFormFieldModule,
    ReactiveFormsModule,
    MatLegacyCheckboxModule,
    MatIconModule,
    MatLegacyChipsModule,
    MatLegacySelectModule,
  ],
  templateUrl: './report-schedule-form.component.html',
  styleUrl: './report-schedule-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportScheduleFormComponent implements OnInit {
  @Input() set reportLayout(value: ReportLayout<any>) {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
      this.isEditable = value.permissions.is_editable;
      this.recipientEmails = value.recipients ? value.recipients : [];
      this.setDaysOfWeek(value?.scheduled_days_of_week);
    }
  }

  @Output() scheduleChanged = new EventEmitter<Partial<ReportLayout<any>>>();

  separatorKeysCodes: number[] = [ENTER, COMMA];
  recipientTagCtrl = new UntypedFormControl();
  form = new FormGroup({
    auto_send: new FormControl(false),
    scheduled_period: new FormControl('weekly'),
    scheduled_days_of_week: new FormControl([]),
    scheduled_time_of_day: new FormControl(null),
    scheduled_report_format: new FormControl('pdf'),
    section_total_format: new FormControl('sum'),
    recipients: new FormControl([]),
  });
  daysOfWeek: DaysOfWeek[] = [
    { id: 0, name: 'S' },
    { id: 1, name: 'M' },
    { id: 2, name: 'T' },
    { id: 3, name: 'W' },
    { id: 4, name: 'T' },
    { id: 5, name: 'F' },
    { id: 6, name: 'S' },
  ];
  daysOfWeekCollection: Map<number, boolean> = new Map<number, boolean>();
  recipientEmails: any[] = [];
  isEditable = false;

  private destroyRef = inject(DestroyRef);

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    this.setInitialDaysOfWeek();
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300)).subscribe((value) => {
      this.scheduleChanged.emit(value);
    });
  }

  removeRecipientEmail(email: string): void {
    this.recipientEmails.splice(
      this.recipientEmails.findIndex((item) => item === email),
      1,
    );
    this.form.get('recipients').patchValue(this.recipientEmails);
  }

  addRecipientEmail(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      this.recipientEmails.push(value.trim());
    }

    if (input) {
      input.value = '';
    }

    this.recipientTagCtrl.setValue(null);
    this.form.get('recipients').patchValue(this.recipientEmails);
  }

  dayOfWeekIsSelected(day: number): boolean {
    return this.daysOfWeekCollection.get(day);
  }

  setDaysOfWeek(daysOfWeek: number[]): void {
    if (daysOfWeek?.length) {
      daysOfWeek.forEach((day) => {
        this.daysOfWeekCollection.set(day, true);
      });
    }
    this.changeDetectorRef.detectChanges();
  }

  setInitialDaysOfWeek(): void {
    this.daysOfWeek.forEach((day) => this.daysOfWeekCollection.set(day.id, false));
  }

  toggleDayOfWeek(event: Event, day: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.daysOfWeekCollection.set(day, !this.daysOfWeekCollection.get(day));
    this.form.get('scheduled_days_of_week').setValue(this.getSelectedDaysOfWeek());
  }

  getSelectedDaysOfWeek(): number[] {
    const selected = <number[]>[];
    Array.from(this.daysOfWeekCollection.entries(), (v: [number, boolean]) => {
      if (v['1']) {
        selected.push(v['0']);
      }
    });
    return selected;
  }
}
